import Banner from "@/components/Banner";

import { type FacilitySearchResult } from "../../queries";
import { formatFacilityPhone } from "../../_utils/formatFacilityPhone";

function DuplicateWarning({ candidates }: { candidates: FacilitySearchResult[] }) {
  if (candidates.length === 0) return null;

  return (
    <Banner
      inline
      variant="warn"
      description="Potential duplicate facilities found."
      subText={candidates.map((candidate) => {
        const address = candidate.mainAddress;
        const addressText = address
          ? [address.address1, address.city, address.state, address.postalCode].filter(Boolean).join(", ")
          : "No address on file";
        const formattedPhone = formatFacilityPhone(candidate);
        const phoneText = formattedPhone ? `; ${formattedPhone}` : "";
        return (
          <>
            <a
              href={`/facilities/${candidate.id}/details/contact?return_to=/facilities`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {candidate.name}
            </a>
            {` - ${addressText}${phoneText}`}
          </>
        );
      })}
    />
  );
}

interface CreateFacilityAlertsProps {
  bannerError: string;
  duplicateCandidates: FacilitySearchResult[];
  duplicateCheckError: string;
}

export default function CreateFacilityAlerts({
  bannerError,
  duplicateCandidates,
  duplicateCheckError
}: CreateFacilityAlertsProps) {
  const hasAlerts = !!bannerError || duplicateCandidates.length > 0 || !!duplicateCheckError;

  if (!hasAlerts) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 mb-6">
      {!!bannerError && (
        <Banner inline variant="error" description="The facility could not be created." subText={bannerError} />
      )}
      {duplicateCandidates.length > 0 && <DuplicateWarning candidates={duplicateCandidates} />}
      {!!duplicateCheckError && (
        <Banner inline variant="warn" description="Duplicate check unavailable." subText={duplicateCheckError} />
      )}
    </div>
  );
}
